<template>
  <!-- Begin page -->
  <div id="layout-wrapper" style="margin-top: 100px !important;">
    <NavigationBar/>
    <div class="content-minimize">
      <div class="container-fluid">
        <!-- Page title -->
        <div class="row">
          <div class="col-12">
            <div class="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 class="mb-sm-0 font-size-18">Department Management</h4>
              <div class="page-title-right">
                <ol class="breadcrumb m-0">
                  <li class="breadcrumb-item"><a href="index.html">Dashboard</a></li>
                  <li class="breadcrumb-item active">Departments</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <!-- end page title -->

        <!-- stats cards -->
        <div class="row mb-4">
          <!-- Total Departments -->
          <div class="col-lg-3 col-md-6 mb-3">
            <div class="card mini-stats-wid h-100">
              <div class="card-body">
                <div class="d-flex flex-wrap">
                  <div class="me-3">
                    <p class="text-muted mb-2">Total Departments</p>
                    <h5 class="mb-0">{{ stats.totalDepartments }}</h5>
                  </div>
                  <div class="avatar-sm ms-auto">
                    <div class="avatar-title bg-primary rounded-circle text-white font-size-20">
                      <i class="mdi mdi-office-building"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Active Departments -->
          <div class="col-lg-3 col-md-6 mb-3">
            <div class="card mini-stats-wid h-100">
              <div class="card-body">
                <div class="d-flex flex-wrap">
                  <div class="me-3">
                    <p class="text-muted mb-2">Active Departments</p>
                    <h5 class="mb-0">{{ stats.activeDepartments }}</h5>
                  </div>
                  <div class="avatar-sm ms-auto">
                    <div class="avatar-title bg-success rounded-circle text-white font-size-20">
                      <i class="mdi mdi-check-circle"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Total Employees -->
          <div class="col-lg-3 col-md-6 mb-3">
            <div class="card mini-stats-wid h-100">
              <div class="card-body">
                <div class="d-flex flex-wrap">
                  <div class="me-3">
                    <p class="text-muted mb-2">Total Employees</p>
                    <h5 class="mb-0">{{ stats.totalEmployees }}</h5>
                  </div>
                  <div class="avatar-sm ms-auto">
                    <div class="avatar-title bg-info rounded-circle text-white font-size-20">
                      <i class="mdi mdi-account-group"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Average Budget -->
          <div class="col-lg-3 col-md-6 mb-3">
            <div class="card mini-stats-wid h-100">
              <div class="card-body">
                <div class="d-flex flex-wrap">
                  <div class="me-3">
                    <p class="text-muted mb-2">Average Budget</p>
                    <h5 class="mb-0">{{ formatCurrency(stats.averageBudget) }}</h5>
                  </div>
                  <div class="avatar-sm ms-auto">
                    <div class="avatar-title bg-warning rounded-circle text-white font-size-20">
                      <i class="mdi mdi-currency-usd"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Search, filters and add button -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="card">
              <div class="card-body py-3">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <!-- Search box -->
                  <div class="mb-2 mb-md-0">
                    <div class="search-box">
                      <div class="position-relative">
                        <input v-model="search" @input="filterDepartments"
                               class="form-control bg-light border-light rounded"
                               placeholder="Search departments..." spellcheck="false" type="text">
                        <i class="bx bx-search-alt search-icon"></i>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex align-items-center flex-wrap">
                    <!-- Location filter -->
                    <div class="me-3 mb-2 mb-md-0">
                      <div class="input-group">
                        <label class="input-group-text">Location</label>
                        <select v-model="selectedLocation" @change="filterDepartments"
                                class="form-select" style="min-width: 200px;">
                          <option selected value="">All Locations</option>
                          <option v-for="location in locations" :key="location" :value="location">
                            {{ location }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <!-- Add Department button -->
                    <button class="btn btn-primary mb-2 mb-md-0 me-2"
                            @click="openAddModal"
                            data-bs-target="#addDepartmentModal" data-bs-toggle="modal">
                      <i class="bx bx-plus me-1"></i> Add Department
                    </button>

                    <!-- Download button -->
                    <button class="btn btn-success mb-2 mb-md-0" @click="downloadReport">
                      <i class="bx bxs-download me-1"></i> Download
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Department data table -->
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table align-middle table-hover contacts-table table-striped">
                    <thead class="table-light">
                    <tr>
                      <th>Department Name</th>
                      <th>Location</th>
                      <th>Manager</th>
                      <th>Employees</th>
                      <th>Budget</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="loading">
                      <td colspan="7" class="text-center py-4">
                        <div class="spinner-border text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                    <tr v-else-if="filteredDepartments.length === 0">
                      <td colspan="7" class="text-center py-4">
                        No departments found matching your criteria
                      </td>
                    </tr>
                    <tr v-for="department in filteredDepartments" :key="department.id">
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="avatar-xs me-3">
                            <div class="avatar-title rounded-circle bg-soft-primary text-primary">
                              {{ department.departmentName.charAt(0).toUpperCase() }}
                            </div>
                          </div>
                          <div>
                            <h6 class="mb-0">{{ department.departmentName }}</h6>
                            <p class="text-muted mb-0 small">{{ department.description || 'No description' }}</p>
                          </div>
                        </div>
                      </td>
                      <td>{{ department.location || 'Not specified' }}</td>
                      <td>
                        <div v-if="department.manager">
                          <h6 class="mb-0">{{ department.manager }}</h6>
                          <p class="text-muted mb-0 small">
                            <i class="mdi mdi-email me-1"></i>{{ department.managerEmail || '' }}
                          </p>
                        </div>
                        <span v-else class="text-muted">No manager assigned</span>
                      </td>
                      <td>
                        <span class="badge bg-info fs-6">{{ department.employeeCount || 0 }}</span>
                      </td>
                      <td>{{ formatCurrency(department.budget) }}</td>
                      <td>
                        <span class="badge" :class="getStatusBadgeClass(department.status)">
                          {{ department.status || 'Active' }}
                        </span>
                      </td>
                      <td>
                        <div class="btn-group" role="group">
                          <button class="btn btn-primary btn-sm"
                                  @click="viewDepartment(department)"
                                  data-bs-target="#viewDepartmentModal" data-bs-toggle="modal">
                            <i class="mdi mdi-eye"></i>
                          </button>
                          <button class="btn btn-warning btn-sm"
                                  @click="editDepartment(department)"
                                  data-bs-target="#editDepartmentModal" data-bs-toggle="modal">
                            <i class="mdi mdi-pencil"></i>
                          </button>
                          <button class="btn btn-danger btn-sm"
                                  @click="confirmDelete(department)">
                            <i class="mdi mdi-delete"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div class="row" v-if="pagination.totalPages > 1">
                  <div class="col-12">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        Showing {{ ((pagination.currentPage - 1) * pagination.limit) + 1 }} to
                        {{ Math.min(pagination.currentPage * pagination.limit, pagination.totalCount) }}
                        of {{ pagination.totalCount }} entries
                      </div>
                      <nav>
                        <ul class="pagination pagination-sm mb-0">
                          <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
                            <button class="page-link" @click="changePage(pagination.currentPage - 1)">Previous</button>
                          </li>
                          <li v-for="page in getVisiblePages()" :key="page"
                              class="page-item" :class="{ active: page === pagination.currentPage }">
                            <button class="page-link" @click="changePage(page)">{{ page }}</button>
                          </li>
                          <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
                            <button class="page-link" @click="changePage(pagination.currentPage + 1)">Next</button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Add Department Modal -->
      <div aria-hidden="true" aria-labelledby="addDepartmentModalLabel" class="modal fade"
           id="addDepartmentModal" tabindex="-1">
        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content shadow-lg">
            <div class="modal-header bg-primary text-white">
              <h5 class="modal-title" id="addDepartmentModalLabel">
                <i class="mdi mdi-plus me-2"></i>Add New Department
              </h5>
              <button aria-label="Close" class="btn-close btn-close-white"
                      data-bs-dismiss="modal" type="button"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="addDepartment">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label" for="departmentName">Department Name <span class="text-danger">*</span></label>
                      <input v-model="newDepartment.departmentName" class="form-control"
                             id="departmentName" placeholder="Enter department name" required type="text">
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label" for="location">Location</label>
                      <input v-model="newDepartment.location" class="form-control"
                             id="location" placeholder="Enter location" type="text">
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <div class="mb-3">
                      <label class="form-label" for="managerId">Manager</label>
                      <select v-model="newDepartment.managerId" @change="onManagerChange('new')"
                              class="form-select" id="managerId">
                        <option value="">Select Manager (Optional)</option>
                        <option v-for="manager in availableManagers" :key="manager.id" :value="manager.id">
                          {{ manager.firstName }} {{ manager.lastName }}
                          <span v-if="manager.designation"> - {{ manager.designation }}</span>
                          <span v-if="manager.departmentName"> ({{ manager.departmentName }})</span>
                        </option>
                      </select>
                      <small class="form-text text-muted">
                        <div v-if="loadingManagers" class="text-info">
                          <i class="mdi mdi-loading mdi-spin me-1"></i>Loading managers...
                        </div>
                        <div v-else-if="selectedManagerInfo.new">
                          Selected: {{ selectedManagerInfo.new.firstName }} {{ selectedManagerInfo.new.lastName }}
                          <br>Email: {{ selectedManagerInfo.new.email || 'Not available' }}
                          <br>Phone: {{ selectedManagerInfo.new.phone || 'Not available' }}
                        </div>
                      </small>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label" for="budget">Budget</label>
                      <div class="input-group">
                        <select v-model="newDepartment.currency" class="form-select" style="max-width: 100px;">
                          <option value="KES">KES</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                        </select>
                        <input v-model="newDepartment.budget" class="form-control"
                               id="budget" placeholder="Enter budget amount" type="number" step="0.01">
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <!-- Empty column for alignment -->
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label" for="description">Description</label>
                  <textarea v-model="newDepartment.description" class="form-control"
                            id="description" placeholder="Enter department description" rows="3"></textarea>
                </div>
              </form>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" data-bs-dismiss="modal" type="button">
                <i class="mdi mdi-close me-2"></i>Cancel
              </button>
              <button class="btn btn-primary" @click="addDepartment" :disabled="addingDepartment">
                <span v-if="addingDepartment" class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="mdi mdi-check me-2"></i>Add Department
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Edit Department Modal -->
      <div aria-hidden="true" aria-labelledby="editDepartmentModalLabel" class="modal fade"
           id="editDepartmentModal" tabindex="-1">
        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content shadow-lg">
            <div class="modal-header text-white">
              <h5 class="modal-title" id="editDepartmentModalLabel">
                <i class="mdi mdi-pencil me-2"></i>Edit Department
              </h5>
              <button aria-label="Close" class="btn-close btn-close-black"
                      data-bs-dismiss="modal" type="button"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="updateDepartment" v-if="editingDepartment">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label" for="editDepartmentName">Department Name <span class="text-danger">*</span></label>
                      <input v-model="editingDepartment.departmentName" class="form-control"
                             id="editDepartmentName" required type="text">
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label" for="editLocation">Location</label>
                      <input v-model="editingDepartment.location" class="form-control"
                             id="editLocation" type="text">
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <div class="mb-3">
                      <label class="form-label" for="editManagerId">Manager</label>
                      <select v-model="editingDepartment.managerId" @change="onManagerChange('edit')"
                              class="form-select" id="editManagerId">
                        <option value="">Select Manager (Optional)</option>
                        <option v-for="manager in availableManagers" :key="manager.id" :value="manager.id">
                          {{ manager.firstName }} {{ manager.lastName }}
                          <span v-if="manager.designation"> - {{ manager.designation }}</span>
                          <span v-if="manager.departmentName"> ({{ manager.departmentName }})</span>
                        </option>
                      </select>
                      <small class="form-text text-muted">
                        <div v-if="selectedManagerInfo.edit">
                          Selected: {{ selectedManagerInfo.edit.firstName }} {{ selectedManagerInfo.edit.lastName }}
                          <br>Email: {{ selectedManagerInfo.edit.email || 'Not available' }}
                          <br>Phone: {{ selectedManagerInfo.edit.phone || 'Not available' }}
                        </div>
                      </small>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label" for="editBudget">Budget</label>
                      <div class="input-group">
                        <select v-model="editingDepartment.currency" class="form-select" style="max-width: 100px;">
                          <option value="KES">KES</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                        </select>
                        <input v-model="editingDepartment.budget" class="form-control"
                               id="editBudget" type="number" step="0.01">
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <!-- Empty column for alignment -->
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label" for="editDescription">Description</label>
                  <textarea v-model="editingDepartment.description" class="form-control"
                            id="editDescription" rows="3"></textarea>
                </div>
              </form>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" data-bs-dismiss="modal" type="button">
                <i class="mdi mdi-close me-2"></i>Cancel
              </button>
              <button class="btn btn-warning" @click="updateDepartment" :disabled="updatingDepartment">
                <span v-if="updatingDepartment" class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="mdi mdi-check me-2"></i>Update Department
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- View Department Modal -->
      <div aria-hidden="true" aria-labelledby="viewDepartmentModalLabel" class="modal fade"
           id="viewDepartmentModal" tabindex="-1">
        <div class="modal-dialog modal-xl modal-dialog-centered">
          <div class="modal-content shadow-lg">
            <div class="modal-header text-white">
              <h5 class="modal-title" id="viewDepartmentModalLabel">
                <i class="mdi mdi-eye me-2"></i>Department Details
              </h5>
              <button aria-label="Close" class="btn-close btn-close-black"
                      data-bs-dismiss="modal" type="button"></button>
            </div>
            <div class="modal-body p-4" v-if="selectedDepartment">
              <div class="row">
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h6 class="card-title mb-0">Basic Information</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <label class="text-muted">Department Name:</label>
                        <p class="mb-0 fw-semibold">{{ selectedDepartment.departmentName }}</p>
                      </div>
                      <div class="mb-3">
                        <label class="text-muted">Location:</label>
                        <p class="mb-0">{{ selectedDepartment.location || 'Not specified' }}</p>
                      </div>
                      <div class="mb-3">
                        <label class="text-muted">Budget:</label>
                        <p class="mb-0">{{ formatCurrency(selectedDepartment.budget) }}</p>
                      </div>
                      <div class="mb-3">
                        <label class="text-muted">Created:</label>
                        <p class="mb-0">{{ formatDate(selectedDepartment.createdAt) }}</p>
                      </div>
                      <div class="mb-3">
                        <label class="text-muted">Last Updated:</label>
                        <p class="mb-0">{{ formatDate(selectedDepartment.updatedAt) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h6 class="card-title mb-0">Manager Information</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <label class="text-muted">Manager:</label>
                        <p class="mb-0">{{ selectedDepartment.manager || 'No manager assigned' }}</p>
                      </div>
                      <div class="mb-3">
                        <label class="text-muted">Email:</label>
                        <p class="mb-0">{{ selectedDepartment.managerEmail || 'Not provided' }}</p>
                      </div>
                      <div class="mb-3">
                        <label class="text-muted">Phone:</label>
                        <p class="mb-0">{{ selectedDepartment.managerPhone || 'Not provided' }}</p>
                      </div>
                      <div class="mb-3">
                        <label class="text-muted">Employees:</label>
                        <p class="mb-0">
                          <span class="badge bg-info fs-6">{{ selectedDepartment.employeeCount || 0 }}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-12">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="card-title mb-0">Description</h6>
                    </div>
                    <div class="card-body">
                      <p class="mb-0">{{ selectedDepartment.description || 'No description provided' }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Department Employees -->
              <div class="row mt-3" v-if="departmentEmployees.length > 0">
                <div class="col-12">
                  <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      <h6 class="card-title mb-0">Department Employees</h6>
                      <span class="badge bg-primary">{{ departmentEmployees.length }}</span>
                    </div>
                    <div class="card-body p-0">
                      <div class="table-responsive">
                        <table class="table table-sm mb-0">
                          <thead class="table-light">
                          <tr>
                            <th>Employee ID</th>
                            <th>Name</th>
                            <th>Position</th>
                            <th>Email</th>
                            <th>Status</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="employee in departmentEmployees" :key="employee.id">
                            <td>{{ employee.empNo || employee.nationalId }}</td>
                            <td>{{ employee.firstName }} {{ employee.lastName }}</td>
                            <td>{{ employee.title || employee.designation || 'N/A' }}</td>
                            <td>{{ employee.email }}</td>
                            <td>
                                <span class="badge" :class="getEmployeeStatusBadge(employee.status)">
                                  {{ employee.status }}
                                </span>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" data-bs-dismiss="modal" type="button">
                <i class="mdi mdi-close me-2"></i>Close
              </button>
              <button class="btn btn-warning" @click="editDepartment(selectedDepartment)"
                      data-bs-target="#editDepartmentModal" data-bs-toggle="modal">
                <i class="mdi mdi-pencil me-2"></i>Edit Department
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div aria-hidden="true" aria-labelledby="deleteConfirmModalLabel" class="modal fade"
           id="deleteConfirmModal" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content shadow-lg">
            <div class="modal-header  text-white">
              <h5 class="modal-title" id="deleteConfirmModalLabel">
                <i class="mdi mdi-alert me-2"></i>Confirm Delete
              </h5>
              <button aria-label="Close" class="btn-close btn-close-black"
                      data-bs-dismiss="modal" type="button"></button>
            </div>
            <div class="modal-body">
              <div class="text-center">
                <div class="mb-3">
                  <i class="mdi mdi-alert-circle text-danger" style="font-size: 3rem;"></i>
                </div>
                <h5>Are you sure?</h5>
                <p class="mb-0">
                  You are about to delete <strong>{{ departmentToDelete?.departmentName }}</strong>.
                  This action cannot be undone.
                </p>
                <div class="alert alert-warning mt-3" v-if="departmentToDelete?.employeeCount > 0">
                  <strong>Warning:</strong> This department has {{ departmentToDelete.employeeCount }} employees.
                  Please reassign them before deleting.
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" data-bs-dismiss="modal" type="button">
                <i class="mdi mdi-close me-2"></i>Cancel
              </button>
              <button class="btn btn-danger" @click="deleteDepartment" :disabled="deletingDepartment">
                <span v-if="deletingDepartment" class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="mdi mdi-delete me-2"></i>Delete Department
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- End Page-content -->
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { execute, authUrl } from "@/api";
import moment from "moment/moment";
import * as XLSX from 'xlsx';

export default {
  name: "DepartmentManagement",
  components: {
    NavigationBar
  },
  data() {
    return {
      departments: [],
      filteredDepartments: [],
      locations: [],
      selectedLocation: "",
      search: "",
      selectedDepartment: null,
      departmentEmployees: [],
      departmentToDelete: null,
      editingDepartment: null,
      availableManagers: [],
      selectedManagerInfo: {
        new: null,
        edit: null
      },
      newDepartment: {
        departmentName: '',
        location: '',
        managerId: '',
        budget: '',
        currency: 'KES',
        description: ''
      },
      stats: {
        totalDepartments: 0,
        activeDepartments: 0,
        totalEmployees: 0,
        averageBudget: 0
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10
      },
      loading: false,
      loadingManagers: false,
      addingDepartment: false,
      updatingDepartment: false,
      deletingDepartment: false,
      loadingEmployees: false,
      error: null
    };
  },
  mounted() {
    this.fetchDepartments();
    this.fetchAvailableManagers();
  },
  methods: {
    fetchDepartments() {
      this.loading = true;
      this.departments = [];
      this.filteredDepartments = [];

      const data = new FormData();
      data.append("function", "getDepartmentsWithDetails");
      data.append("page", this.pagination.currentPage);
      data.append("limit", this.pagination.limit);

      if (this.search) {
        data.append("search", this.search);
      }

      if (this.selectedLocation) {
        data.append("location", this.selectedLocation);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.departments = Array.isArray(res.data.data.departments) ? res.data.data.departments : [];
              this.stats = res.data.data.stats || {
                totalDepartments: 0,
                activeDepartments: 0,
                totalEmployees: 0,
                averageBudget: 0
              };
              this.locations = Array.isArray(res.data.data.locations) ? res.data.data.locations : [];
              this.pagination = res.data.data.pagination || this.pagination;
              this.filterDepartments();
            } else {
              this.error = res.data.message || "Failed to fetch departments";
              this.departments = [];
              this.filteredDepartments = [];
            }
          })
          .catch((e) => {
            this.loading = false;
            this.error = e.message || "An error occurred while fetching departments";
            console.error("Error fetching departments:", e);
            this.departments = [];
            this.filteredDepartments = [];
          });
    },

    fetchAvailableManagers() {
      this.loadingManagers = true;
      this.availableManagers = [];

      const data = new FormData();
      data.append("function", "getPotentialManagers");

      execute(data, authUrl)
          .then((res) => {
            this.loadingManagers = false;
            if (res.data.success) {
              this.availableManagers = Array.isArray(res.data.data.managers) ? res.data.data.managers : [];
            } else {
              this.error = res.data.message || "Failed to fetch potential managers";
              this.availableManagers = [];
            }
          })
          .catch((e) => {
            this.loadingManagers = false;
            this.error = e.message || "An error occurred while fetching managers";
            console.error("Error fetching managers:", e);
            this.availableManagers = [];
          });
    },

    onManagerChange(type) {
      const managerId = type === 'new' ? this.newDepartment.managerId : this.editingDepartment.managerId;

      if (!managerId) {
        this.selectedManagerInfo[type] = null;
        return;
      }

      // Find the selected manager from the available managers list
      const selectedManager = this.availableManagers.find(manager => manager.id == managerId);
      if (selectedManager) {
        this.selectedManagerInfo[type] = selectedManager;
      } else {
        // If not found in the list, fetch from API
        this.getManagerDetails(managerId, type);
      }
    },

    getManagerDetails(managerId, type) {
      const data = new FormData();
      data.append("function", "getManagerDetails");
      data.append("managerId", managerId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedManagerInfo[type] = res.data.data.manager;
            } else {
              this.selectedManagerInfo[type] = null;
              console.error("Failed to fetch manager details:", res.data.message);
            }
          })
          .catch((e) => {
            this.selectedManagerInfo[type] = null;
            console.error("Error fetching manager details:", e);
          });
    },

    openAddModal() {
      // Reset form and manager info when opening add modal
      this.newDepartment = {
        departmentName: '',
        location: '',
        managerId: '',
        budget: '',
        currency: 'KES',
        description: ''
      };
      this.selectedManagerInfo.new = null;

      // Refresh managers list
      if (this.availableManagers.length === 0) {
        this.fetchAvailableManagers();
      }
    },

    filterDepartments() {
      if (!Array.isArray(this.departments)) {
        this.filteredDepartments = [];
        return;
      }

      let filtered = [...this.departments];

      // Filter by search term
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(dept =>
            (dept.departmentName && dept.departmentName.toLowerCase().includes(searchLower)) ||
            (dept.manager && dept.manager.toLowerCase().includes(searchLower)) ||
            (dept.location && dept.location.toLowerCase().includes(searchLower))
        );
      }

      this.filteredDepartments = filtered;
    },

    viewDepartment(department) {
      this.selectedDepartment = { ...department };
      this.getDepartmentEmployees(department.id);
    },

    getDepartmentEmployees(departmentId) {
      this.loadingEmployees = true;
      this.departmentEmployees = [];

      const data = new FormData();
      data.append("function", "getDepartmentEmployees");
      data.append("departmentId", departmentId);

      execute(data, authUrl)
          .then((res) => {
            this.loadingEmployees = false;
            if (res.data.success) {
              this.departmentEmployees = Array.isArray(res.data.data.employees) ? res.data.data.employees : [];
            } else {
              this.error = res.data.message || "Failed to fetch department employees";
              this.departmentEmployees = [];
            }
          })
          .catch((e) => {
            this.loadingEmployees = false;
            this.error = e.message || "An error occurred while fetching employees";
            console.error("Error fetching employees:", e);
            this.departmentEmployees = [];
          });
    },

    editDepartment(department) {
      this.editingDepartment = { ...department };
      this.selectedManagerInfo.edit = null;

      // If department has a manager, try to find and set the manager ID
      if (department.manager) {
        const manager = this.availableManagers.find(m =>
            `${m.firstName} ${m.lastName}` === department.manager
        );
        if (manager) {
          this.editingDepartment.managerId = manager.id;
          this.selectedManagerInfo.edit = manager;
        }
      }
    },

    addDepartment() {
      if (!this.newDepartment.departmentName.trim()) {
        alert("Department name is required");
        return;
      }

      this.addingDepartment = true;

      const data = new FormData();
      data.append("function", "addDepartmentWithManager");
      Object.keys(this.newDepartment).forEach(key => {
        data.append(key, this.newDepartment[key]);
      });

      execute(data, authUrl)
          .then((res) => {
            this.addingDepartment = false;
            if (res.data.success) {
              // Close modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('addDepartmentModal'));
              modal.hide();

              // Reset form
              this.newDepartment = {
                departmentName: '',
                location: '',
                managerId: '',
                budget: '',
                currency: 'KES',
                description: ''
              };
              this.selectedManagerInfo.new = null;

              // Refresh departments
              this.fetchDepartments();

              // Show success message
              this.showSuccessMessage("Department added successfully");
            } else {
              this.error = res.data.message || "Failed to add department";
              alert(this.error);
            }
          })
          .catch((e) => {
            this.addingDepartment = false;
            this.error = e.message || "An error occurred while adding department";
            console.error("Error adding department:", e);
            alert(this.error);
          });
    },

    updateDepartment() {
      if (!this.editingDepartment.departmentName.trim()) {
        alert("Department name is required");
        return;
      }

      this.updatingDepartment = true;

      const data = new FormData();
      data.append("function", "updateDepartmentWithManager");
      Object.keys(this.editingDepartment).forEach(key => {
        data.append(key, this.editingDepartment[key]);
      });

      execute(data, authUrl)
          .then((res) => {
            this.updatingDepartment = false;
            if (res.data.success) {
              // Close modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('editDepartmentModal'));
              modal.hide();

              // Refresh departments
              this.fetchDepartments();

              // Show success message
              this.showSuccessMessage("Department updated successfully");

              this.editingDepartment = null;
              this.selectedManagerInfo.edit = null;
            } else {
              this.error = res.data.message || "Failed to update department";
              alert(this.error);
            }
          })
          .catch((e) => {
            this.updatingDepartment = false;
            this.error = e.message || "An error occurred while updating department";
            console.error("Error updating department:", e);
            alert(this.error);
          });
    },

    confirmDelete(department) {
      this.departmentToDelete = department;
      const modal = new bootstrap.Modal(document.getElementById('deleteConfirmModal'));
      modal.show();
    },

    deleteDepartment() {
      if (!this.departmentToDelete) return;

      this.deletingDepartment = true;

      const data = new FormData();
      data.append("function", "deleteDepartment");
      data.append("departmentId", this.departmentToDelete.id);

      execute(data, authUrl)
          .then((res) => {
            this.deletingDepartment = false;
            if (res.data.success) {
              // Close modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('deleteConfirmModal'));
              modal.hide();

              // Refresh departments
              this.fetchDepartments();

              // Show success message
              this.showSuccessMessage("Department deleted successfully");

              this.departmentToDelete = null;
            } else {
              this.error = res.data.message || "Failed to delete department";
              alert(this.error);
            }
          })
          .catch((e) => {
            this.deletingDepartment = false;
            this.error = e.message || "An error occurred while deleting department";
            console.error("Error deleting department:", e);
            alert(this.error);
          });
    },

    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page;
        this.fetchDepartments();
      }
    },

    getVisiblePages() {
      const current = this.pagination.currentPage;
      const total = this.pagination.totalPages;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];

      for (let i = Math.max(2, current - delta);
           i <= Math.min(total - 1, current + delta);
           i++) {
        range.push(i);
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...');
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total);
      } else {
        rangeWithDots.push(total);
      }

      return rangeWithDots.filter((page, index, arr) => arr.indexOf(page) === index);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('DD MMM YYYY');
    },

    formatCurrency(amount) {
      if (!amount || isNaN(amount)) return 'N/A';
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES'
      }).format(amount);
    },

    getStatusBadgeClass(status) {
      switch (status?.toLowerCase()) {
        case 'active':
          return 'bg-success';
        case 'inactive':
          return 'bg-secondary';
        case 'suspended':
          return 'bg-warning';
        default:
          return 'bg-success';
      }
    },

    getEmployeeStatusBadge(status) {
      switch (status?.toLowerCase()) {
        case 'active':
          return 'bg-success';
        case 'inactive':
          return 'bg-secondary';
        case 'suspended':
          return 'bg-warning';
        case 'terminated':
          return 'bg-danger';
        default:
          return 'bg-primary';
      }
    },

    showSuccessMessage(message) {
      // You can implement a toast notification here
      alert(message);
    },

    downloadReport() {
      try {
        if (!Array.isArray(this.departments) || this.departments.length === 0) {
          alert("No data available for download.");
          return;
        }

        // Create worksheet data
        const worksheetData = [];

        // Add headers
        worksheetData.push([
          'Department ID',
          'Department Name',
          'Location',
          'Manager',
          'Manager Email',
          'Manager Phone',
          'Budget',
          'Currency',
          'Employee Count',
          'Status',
          'Created Date',
          'Description'
        ]);

        // Add data rows
        this.filteredDepartments.forEach(department => {
          worksheetData.push([
            department.id || '',
            department.departmentName || '',
            department.location || '',
            department.manager || '',
            department.managerEmail || '',
            department.managerPhone || '',
            department.budget || '',
            department.currency || 'KES',
            department.employeeCount || 0,
            department.status || 'Active',
            this.formatDate(department.createdAt),
            department.description || ''
          ]);
        });

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(worksheetData);

        // Set column widths
        const wscols = [
          { wch: 10 }, // Department ID
          { wch: 25 }, // Department Name
          { wch: 20 }, // Location
          { wch: 20 }, // Manager
          { wch: 25 }, // Manager Email
          { wch: 15 }, // Manager Phone
          { wch: 15 }, // Budget
          { wch: 10 }, // Currency
          { wch: 12 }, // Employee Count
          { wch: 10 }, // Status
          { wch: 15 }, // Created Date
          { wch: 30 }  // Description
        ];
        ws['!cols'] = wscols;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Departments");

        // Generate filename
        const fileName = `Departments_Report_${moment().format('YYYY-MM-DD')}.xlsx`;

        // Write and download the file
        XLSX.writeFile(wb, fileName);

      } catch (error) {
        console.error('Error generating Excel file:', error);
        alert('Failed to generate Excel file. Please try again.');
      }
    }
  }
};
</script>

<style scoped>
.search-icon {
  position: absolute;
  right: 10px;
  top: 10px;
}

.table-responsive {
  overflow-x: auto;
}

.content-minimize {
  min-height: calc(100vh - 70px);
  padding: 0 15px;
}

.mini-stats-wid {
  height: 100%;
  width: 100%;
}

.card {
  margin-bottom: 0;
}

.avatar-sm, .avatar-xs {
  flex-shrink: 0;
}

.avatar-xs {
  height: 2rem;
  width: 2rem;
}

.avatar-xs .avatar-title {
  font-size: 0.75rem;
  height: 2rem;
  width: 2rem;
}

.badge.fs-6 {
  font-size: 0.9rem !important;
  padding: 0.4rem 0.6rem;
}

.bg-soft-primary {
  background-color: rgba(74, 109, 255, 0.1) !important;
}

.text-primary {
  color: #4a6dff !important;
}

.btn-group .btn {
  margin-right: 0;
}

.form-text {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-text .mdi-loading {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .d-flex.flex-wrap {
    justify-content: space-between;
  }

  .btn-group {
    flex-direction: column;
  }

  .btn-group .btn {
    margin-bottom: 0.25rem;
  }
}

.modal-xl {
  max-width: 90%;
}

.pagination-sm .page-link {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}
</style>